/**
 * Recharge / Deposit page.
 * URL: /recharge
 *
 * Endpoints:
 *   POST /api/recharge — { channel, amount, memo, receipt_url? }
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { rechargeAPI } from '../api/recharge';
import { Button } from '../components/ui/Button';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { ThemedBottomActionBar, ThemedInput, ThemedTextarea, ThemedField, ThemedNotice, ThemedPanel } from '../components/ui/ThemedSurface';
import { CASHFLOW_COLORS as C } from '@/constants';

// ---- Types ------------------------------------------------------------------

interface RechargeBody {
  channel: number;
  amount: string;
  memo: string;
  receipt_url?: string;
}

// ---- Icons ------------------------------------------------------------------

function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

// ---- Channel Tab ------------------------------------------------------------

// ---- Component --------------------------------------------------------------

export function RechargePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const CHANNELS = [
    t('recharge.bankTransfer'),
    t('recharge.usdtTrc20'),
    t('recharge.platformRecharge'),
  ];

  const [selectedChannel, setSelectedChannel] = useState(0);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const bankAccount = '0123-456-789012';
  const usdtAddress = 'TWxxxxxxxxxxxxxxxxxxxxx';

  function handleCopy(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceiptFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setReceiptPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  }

  function handleChannelChange(channel: number) {
    if (channel === selectedChannel) return;
    setSelectedChannel(channel);
    setAmount('');
    setMemo('');
    setReceiptFile(null);
    setReceiptPreview(null);
    setError(null);
    setSuccess(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const rechargeMut = useMutation({
    mutationFn: (body: RechargeBody) => rechargeAPI.create(body as unknown as Parameters<typeof rechargeAPI.create>[0]),
    onSuccess: () => {
      setSuccess(true);
      setError(null);
      setTimeout(() => navigate(-1), 1800);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('recharge.submitError'));
    },
  });

  function handleSubmit() {
    setError(null);
    if (!amount || Number(amount) <= 0) {
      setError(t('recharge.invalidAmount'));
      return;
    }
    rechargeMut.mutate({
      channel: selectedChannel,
      amount: amount.trim(),
      memo: memo.trim(),
      receipt_url: receiptPreview ?? undefined,
    });
  }

  const amountRequiredFilled = amount.trim() !== '' && Number(amount) > 0;
  const memoRequiredFilled = selectedChannel !== 0 || memo.trim() !== '';
  const receiptRequiredFilled = selectedChannel !== 0 || Boolean(receiptPreview);
  const canSubmit = amountRequiredFilled && memoRequiredFilled && receiptRequiredFilled && !success && !rechargeMut.isPending;

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('recharge.title')} onBack={() => navigate(-1)} theme={C} />

        {/* Channel selector */}
        <div className="px-5 pb-2 pt-[70px] flex flex-col gap-5" style={{ height: 'calc(100% - 88px)', paddingBottom: '84px' }}>
          <ThemedPanel className="p-4 flex flex-col gap-2" theme={C}>
            <div className="text-sm font-semibold" style={{ color: C.text }}>
              {t('recharge.selectMethod')}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {CHANNELS.map((label, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleChannelChange(i)}
                  className={`rounded-lg border px-2 py-3 text-xs font-semibold transition-all active:scale-95 ${
                    selectedChannel === i
                      ? 'shadow-[0_0_14px_rgba(245,236,99,0.20)]'
                      : 'opacity-80 hover:opacity-100'
                  }`}
                  style={{
                    background: selectedChannel === i ? C.chipBg : C.inputBg,
                    borderColor: selectedChannel === i ? C.gold : C.softBorder,
                    color: selectedChannel === i ? C.gold : C.muted,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </ThemedPanel>

          {/* ── Bank Transfer ── */}
          {selectedChannel === 0 && (
            <>
              {/* Bank info box */}
              <ThemedPanel className="space-y-2 p-4" theme={C}>
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: C.muted }}>{t('recharge.accountName')}</span>
                  <span className="font-semibold" style={{ color: C.text }}>陈启豪</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: C.muted }}>{t('recharge.bank')}</span>
                  <span className="font-semibold" style={{ color: C.text }}>中国信托银行</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: C.muted }}>{t('recharge.accountNo')}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold" style={{ color: C.text }}>{bankAccount}</span>
                    <button
                      type="button"
                      onClick={() => handleCopy(bankAccount)}
                      className="transition-all active:scale-90"
                      style={{ color: C.green }}
                      aria-label={t('common.copy')}
                    >
                      <CopyIcon />
                    </button>
                  </div>
                </div>
                {copied && (
                  <p className="text-right text-xs" style={{ color: C.green }}>{t('common.copied')}</p>
                )}
              </ThemedPanel>

              <ThemedPanel className="p-4 flex flex-col gap-4" theme={C}>
                {/* Amount */}
                <ThemedField label={t('recharge.amount')} required>
                  <div className="relative">
                    <ThemedInput
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={t('recharge.amountPlaceholder')}
                      min="0"
                      step="any"
                      className="pr-14"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold" style={{ color: C.gold }}>CNY</span>
                  </div>
                </ThemedField>

                {/* Memo */}
                <ThemedField label={t('recharge.memo')} required>
                  <ThemedTextarea
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    placeholder={t('recharge.memoPlaceholder')}
                    rows={3}
                  />
                </ThemedField>

                {/* Upload receipt */}
                <ThemedField label={t('recharge.uploadReceipt')} required>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed py-5 transition-opacity active:opacity-70"
                    style={{ borderColor: C.softBorder, background: C.inputBg, color: C.muted }}
                  >
                    {receiptPreview ? (
                      <img
                        src={receiptPreview}
                        alt="Receipt preview"
                        className="max-h-40 rounded-lg object-contain"
                      />
                    ) : (
                      <>
                        <CameraIcon />
                        <div className="flex flex-col gap-1">
                          <span className="text-sm">{t('recharge.uploadReceipt')}</span>
                          <span className="text-xs opacity-60">{t('recharge.clickToSelect')}</span>
                        </div>
                      </>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </ThemedField>
              </ThemedPanel>
            </>
          )}

          {/* ── USDT TRC20 ── */}
          {selectedChannel === 1 && (
            <>
              {/* QR placeholder */}
              <ThemedPanel className="p-4 flex flex-col gap-4" theme={C}> 
                <div className="text-sm font-semibold" style={{ color: C.text }}>{t('recharge.trcdeposit')}</div>
                <div className="flex justify-center">
                  <div className="mx-auto flex h-44 w-44 items-center justify-center rounded-xl border px-2 text-center text-xs" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.muted }}>
                  {t('recharge.trc20Address')}</div>
                </div>

                {/* Address */}
                <div className="flex flex-col gap-2">
                  <div className="text-xs" style={{ color: C.muted }}>{t('recharge.depositAddress')}</div>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="flex-1 break-all font-mono text-xs" style={{ color: C.gold }}>
                        {usdtAddress}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopy(usdtAddress)}
                        className="shrink-0 active:scale-90 transition-all"
                        style={{ color: C.green }}
                        aria-label={t('common.copy')}
                      >
                        <CopyIcon />
                      </button>
                    </div>
                    {copied && <div className="text-xs" style={{ color: C.green }}>{t('common.copied')}</div>}
                  </div>
                </div>
              </ThemedPanel>


              <ThemedPanel className="p-4" theme={C}>
                {/* Amount */}
                <ThemedField label={t('recharge.amountUsdt')} required>
                  <div className="relative">
                    <ThemedInput
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={t('recharge.amountPlaceholder')}
                      min="0"
                      step="any"
                      className="pr-16"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold" style={{ color: C.gold }}>USDT</span>
                  </div>
                </ThemedField>
              </ThemedPanel>

              {/* Warning */}
              <ThemedNotice tone="warning">{t('recharge.warning')}</ThemedNotice>
            </>
          )}

          {/* ── Platform Recharge ── */}
          {selectedChannel === 2 && (
            <>
              <ThemedPanel className="space-y-3 p-4" theme={C}>
                <div className="text-sm font-semibold" style={{ color: C.text }}>{t('recharge.contactSupportRecharge')}</div>
                <div className="mx-auto flex h-36 w-36 items-center justify-center rounded-xl border px-2 text-center text-xs" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.muted }}>
                  WeChat / Line QR
                </div>
                <p className="text-center text-xs" style={{ color: C.muted }}>{t('recharge.scanQrSupport')}</p>
              </ThemedPanel>

            </>
          )}

          {/* Error */}
          {error && <ThemedNotice tone="error">{error}</ThemedNotice>}

          {/* Success */}
          {success && <ThemedNotice tone="success">{t('recharge.successMsg')}</ThemedNotice>}

        </div>
        <ThemedBottomActionBar background={C.bottomBarBg} className="pb-5 pt-3">
          {selectedChannel === 2 ? (
            <a href="tel:+886000000000" className="block">
              <Button fullWidth size="lg" type="button" className="rounded-xl border text-sm font-medium hover:brightness-110 disabled:brightness-75" style={{ background: C.buttonBg, borderColor: C.buttonBorder, boxShadow: C.buttonShadow, color: C.text }}>
                {t('recharge.contactSupport')}
              </Button>
            </a>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              isLoading={rechargeMut.isPending}
              fullWidth
              size="lg"
              className="rounded-xl border text-sm font-medium hover:brightness-110 disabled:brightness-75"
              style={{ background: C.buttonBg, borderColor: C.buttonBorder, boxShadow: C.buttonShadow, color: C.text }}
            >
              {t('recharge.submit')}
            </Button>
          )}
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}
